import { jwtDecode } from "jwt-decode";

export const getAccessToken = () => localStorage.getItem("accessToken");
export const getRefreshToken = () => localStorage.getItem("refreshToken");

// Mirror the access token in a browser cookie in addition to
// localStorage. The cookie is what nginx-proxy reads when it
// makes the /api/v1/login/verify-grafana-access sub-request before
// proxying /monitoring/* to Grafana — localStorage isn't visible to
// the network layer. Cookie expiry mirrors the token's `exp` claim
// so a stale cookie can't outlive its JWT.
const ACCESS_TOKEN_COOKIE = "access_token";

const setAccessTokenCookie = (token) => {
  try {
    const { exp } = jwtDecode(token);
    const maxAge = Math.max(0, exp - Math.floor(Date.now() / 1000));
    const secure = window.location.protocol === "https:" ? " secure;" : "";
    document.cookie =
      `${ACCESS_TOKEN_COOKIE}=${token}; path=/;${secure} samesite=lax;` +
      ` max-age=${maxAge}`;
  } catch {
    // jwtDecode shouldn't fail on a server-issued token, but if it
    // does we still want localStorage to work — skip the cookie.
  }
};

const clearAccessTokenCookie = () => {
  document.cookie = `${ACCESS_TOKEN_COOKIE}=; path=/; max-age=0`;
};

export const setAccessToken = (token) => {
  localStorage.setItem("accessToken", token);
  setAccessTokenCookie(token);
};
export const setRefreshToken = (token) =>
  localStorage.setItem("refreshToken", token);

export const removeAccessToken = () => {
  localStorage.removeItem("accessToken");
  clearAccessTokenCookie();
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  const { exp } = jwtDecode(token);
  if (!exp) return true;
  return Date.now() >= exp * 1000;
};

export const refreshToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken || isTokenExpired(refreshToken)) {
    throw new Error("Refresh token expired");
  }

  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/login/new_access_token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to refresh token");
  }

  const data = await response.json();
  const { access_token } = data.data;

  setAccessToken(access_token);

  return access_token;
};
